/**
 * ws-asics-configurator.js
 *
 * Verbindet den eingebetteten ASICS-/Spectrum-Konfigurator mit dem bestehenden
 * Shopify-Produktformular. Wird ausschliesslich von snippets/ws-asics-configurator.liquid
 * geladen, also nur auf der Produktvorlage asics-ex-eo-custom-page.
 *
 * Nachrichtenvertrag Version 3. Die Gegenseite ist assets/ws-asics-frame.js,
 * ausgeliefert als Theme-Asset ws-asics-frame.html. Beide Seiten liegen auf
 * derselben Domain; ein eigener Server ist nicht noetig. Aenderungen am Vertrag
 * muessen in beiden Dateien erfolgen.
 *
 * Ablauf seit 05.09.2026 (Vertrag v2):
 *   Der Kunde gestaltet den Schuh und drueckt „In den Warenkorb legen“. Es gibt
 *   keinen eigenen Knopf „Design-ID erstellen“ mehr. Diese Datei faengt den
 *   Kaufklick ab, laesst den Frame speichern, traegt Design-ID, Link und PDF in
 *   das Formular ein, schaltet die Stickerei-Variante um — und loest denselben
 *   Kaufweg danach erneut aus.
 *
 * Grundsatz bleibt: Nur eine von Spectrum tatsaechlich zurueckgegebene Design-ID
 * gilt als Erfolg. Es wird niemals ein Ersatzergebnis erzeugt. Ohne gueltiges
 * Design darf der Hauptschuh auf keinem Kaufweg in den Warenkorb.
 */
(function () {
  'use strict';

  var NS = 'ws-asics-configurator';
  var VERSION = 3;
  var ID_MUSTER = /^[A-Za-z0-9]{8}$/;
  var MIN_HOEHE = 320;
  var MAX_HOEHE = 4000;

  // Die Engine registriert das Design bei Spectrum; das dauert. Der Frame hat
  // eine eigene, kuerzere Frist — diese hier ist nur der Notausstieg, falls der
  // Frame ueberhaupt nicht mehr antwortet.
  var SPEICHER_FRIST = 26000;

  // Wie lange auf die vom Theme aktualisierte Varianten-ID gewartet wird,
  // nachdem die Stickerei-Option umgeschaltet wurde (gemessen: rund 100 ms).
  var VARIANTE_FRIST = 3000;

  var instanzen = new WeakMap();

  function textAus(root, name, ersatz) {
    var wert = root.getAttribute(name);
    return wert === null || wert === '' ? ersatz : wert;
  }

  // Werte aus dem Konfigurator gehen in Warenkorb-Eigenschaften. Sie stammen
  // aus einer Kundeneingabe, deshalb: Laenge begrenzen und Steuerzeichen sowie
  // Zeilenumbrueche entfernen.
  function sauber(wert, maxLaenge) {
    if (typeof wert !== 'string') return '';
    var t = wert.replace(/[\u0000-\u001f\u007f]+/g, ' ').replace(/\s+/g, ' ').trim();
    return t.length > maxLaenge ? t.slice(0, maxLaenge) : t;
  }

  /* ================================================================== *
   * Eine Instanz je Section
   * ================================================================== */

  function Konfigurator(root) {
    this.root = root;
    this.zustand = 'loading';
    this.design = null; // { recipeId, embroidery, thumbnailUrl }
    this.abbau = [];

    // Laeuft gerade ein von uns angestossener Speichervorgang?
    this.speicherLaeuft = false;
    // Welcher Kaufweg wartet auf das Ergebnis? { art: 'form'|'fbt', el }
    this.wartenderKauf = null;
    // Ist der Konfigurator ausgefallen? Dann gilt wieder der manuelle Weg und
    // der Kauf wird nicht mehr abgefangen.
    this.rueckfall = false;
    // Ist die Shopify-Auswahl „Stickerei“ gerade ausgeblendet?
    this.stickereiVersteckt = false;

    this.appUrl = root.getAttribute('data-app-url') || '';
    // Der Frame ist eine HTML-Datei aus den Theme-Assets dieses Shops. Shopify
    // liefert sie in einer Sandbox ohne allow-same-origin aus; ihre Herkunft ist
    // daher "null". Die harte Pruefung ist deshalb der Fenstervergleich
    // (event.source === rahmen.contentWindow) — den kann eine fremde Seite nicht
    // faelschen. Zusaetzlich wird die Herkunft auf "null" oder die eigene
    // eingegrenzt, damit keine beliebige fremde Seite hineinfunkt.
    this.appOrigin = window.location.origin;
    this.formId = root.getAttribute('data-form-id') || '';
    this.optionStickerei = textAus(root, 'data-embroidery-option', 'Stickerei');
    this.wertJa = textAus(root, 'data-embroidery-yes', 'Ja');
    this.wertNein = textAus(root, 'data-embroidery-no', 'Nein');
    this.propId = textAus(root, 'data-prop-id', 'ASICS Design-ID');
    this.propLink = textAus(root, 'data-prop-link', 'Farbvariante (Link)');
    this.propPdf = textAus(root, 'data-prop-pdf', 'ASICS Design-PDF');
    this.propStickText = textAus(root, 'data-prop-emb-text', 'Stickerei-Text');

    this.rahmen = root.querySelector('[data-ws-frame]');
    this.statusKarte = root.querySelector('[data-ws-status]');
    this.statusText = root.querySelector('[data-ws-status-text]');
    this.statusBild = root.querySelector('[data-ws-status-thumb]');
    this.statusId = root.querySelector('[data-ws-status-id]');
    this.statusPdf = root.querySelector('[data-ws-status-pdf]');
    this.hinweis = root.querySelector('[data-ws-hint]');
    this.hinweisStandard = this.hinweis ? this.hinweis.textContent : '';
    this.warenkorbHinweis = root.querySelector('[data-ws-cart-note]');
    // Welches Design haben wir zuletzt in den Warenkorb gelegt?
    this.imWarenkorb = null;
    this.warenkorbPruefung = false;

    this.texte = {};
    try {
      var i18n = root.querySelector('script[type="application/json"][data-ws-i18n]');
      if (i18n) this.texte = JSON.parse(i18n.textContent || '{}');
    } catch (e) {
      this.texte = {};
    }

    this.leseVarianten();
  }

  /* ---------------------------------------------------------------- *
   * Stickerei-Option aus den Varianten ableiten
   *
   * Der Optionsname und seine Werte sind uebersetzbar: das Theme rendert
   * name="options[{{ option.name }}]" und die Werte kommen aus
   * variant.options. Ein fest verdrahtetes „Stickerei“/„Ja“/„Nein“ trifft
   * deshalb in den 20 anderen Sprachen ins Leere — die Variante wuerde nie
   * umgeschaltet und die Stickerei damit verschenkt.
   *
   * Die Varianten-SKUs sind sprachunabhaengig (…-EMB / …-NOEMB). Daraus
   * ergeben sich Optionsindex, Optionsname und die beiden Werte von selbst.
   * Findet sich nichts, bleiben die Werte aus den data-Attributen stehen.
   * ---------------------------------------------------------------- */

  Konfigurator.prototype.leseVarianten = function () {
    this.varianten = null;
    this.stickereiIndex = -1;
    try {
      var el = this.root.querySelector('script[type="application/json"][data-ws-variants]');
      if (!el) return;
      var daten = JSON.parse(el.textContent || '{}');
      var liste = Array.isArray(daten.varianten) ? daten.varianten : [];
      if (!liste.length) return;
      this.varianten = liste;

      var mit = null;
      var ohne = null;
      for (var i = 0; i < liste.length; i++) {
        var sku = String(liste[i].sku || '');
        if (sku.indexOf('-NOEMB') !== -1) {
          if (!ohne) ohne = liste[i];
        } else if (sku.indexOf('-EMB') !== -1) {
          if (!mit) mit = liste[i];
        }
      }
      if (!mit || !ohne || !Array.isArray(mit.o) || !Array.isArray(ohne.o)) return;

      // Der Index, an dem sich ein Paar mit gleicher Groesse unterscheidet.
      var partner = null;
      for (var j = 0; j < liste.length; j++) {
        if (String(liste[j].sku || '') === String(ohne.sku || '').replace('-NOEMB', '-EMB')) {
          partner = liste[j];
          break;
        }
      }
      var a = ohne.o;
      var b = (partner && partner.o) || mit.o;
      for (var k = 0; k < a.length; k++) {
        if (a[k] !== b[k]) {
          this.stickereiIndex = k;
          break;
        }
      }
      if (this.stickereiIndex === -1) return;

      this.wertNein = a[this.stickereiIndex];
      this.wertJa = b[this.stickereiIndex];
      var namen = Array.isArray(daten.optionen) ? daten.optionen : [];
      if (namen[this.stickereiIndex]) this.optionStickerei = namen[this.stickereiIndex];
    } catch (e) {
      this.varianten = null;
      this.stickereiIndex = -1;
    }
  };

  // Welche Varianten-ID gehoert zur aktuellen Auswahl mit dem gewuenschten
  // Stickerei-Wert? Damit laesst sich pruefen, ob das Theme fertig ist.
  Konfigurator.prototype.zielVariante = function (zielWert) {
    if (!this.varianten || this.stickereiIndex === -1) return null;
    var jetzt = this.variantenId();
    var aktuell = null;
    for (var i = 0; i < this.varianten.length; i++) {
      if (String(this.varianten[i].id) === String(jetzt)) { aktuell = this.varianten[i]; break; }
    }
    if (!aktuell || !Array.isArray(aktuell.o)) return null;
    for (var j = 0; j < this.varianten.length; j++) {
      var v = this.varianten[j];
      if (!Array.isArray(v.o) || v.o.length !== aktuell.o.length) continue;
      var passt = true;
      for (var k = 0; k < v.o.length; k++) {
        var soll = k === this.stickereiIndex ? zielWert : aktuell.o[k];
        if (v.o[k] !== soll) { passt = false; break; }
      }
      if (passt) return String(v.id);
    }
    return null;
  };

  Konfigurator.prototype.t = function (schluessel, ersatz) {
    return this.texte[schluessel] || ersatz;
  };

  Konfigurator.prototype.form = function () {
    return this.formId ? document.getElementById(this.formId) : null;
  };

  /* ---------------------------------------------------------------- *
   * Start
   * ---------------------------------------------------------------- */

  Konfigurator.prototype.start = function () {
    if (!this.rahmen || !this.appUrl) {
      // Ohne App bleibt der bisherige manuelle Weg der gueltige Kaufweg.
      this.rueckfall = true;
      this.zeigeManuellesFeld(true);
      this.verbergeStickereiFeld(false);
      this.setzeZustand('error', this.t('app_fehlt', 'Der Konfigurator ist noch nicht eingerichtet.'));
      return;
    }

    // Faellt die App aus, darf die Seite nicht dauerhaft gesperrt bleiben.
    var selbst = this;
    this.ladeWache = setTimeout(function () {
      if (selbst.zustand === 'loading') {
        selbst.rueckfall = true;
        selbst.zeigeManuellesFeld(true);
        selbst.verbergeStickereiFeld(false);
        selbst.setzeZustand('error', selbst.t('app_fehlt', 'Der Konfigurator ist gerade nicht erreichbar. Bitte nutze das Linkfeld weiter unten.'));
      }
    }, 20000);
    this.abbau.push(function () {
      clearTimeout(selbst.ladeWache);
    });

    // Frame-Quelle erst jetzt setzen: so laedt die Engine nur auf dieser Vorlage.
    var url = this.appUrl;
    url += (url.indexOf('?') === -1 ? '?' : '&') + 'embed=1&parentOrigin=' +
      encodeURIComponent(window.location.origin);
    // Versionierte Adressen der Frame-Assets mitgeben. Die HTML-Datei ist ein
    // Theme-Asset ohne Liquid und kann sie nicht selbst bilden; ohne sie liefert
    // die CDN dauerhaft eine alte Kopie.
    var js = this.root.getAttribute('data-frame-js');
    var css = this.root.getAttribute('data-frame-css');
    if (js) url += '&js=' + encodeURIComponent(js);
    if (css) url += '&css=' + encodeURIComponent(css);
    // Sprache mitgeben: die Engine spricht nur Japanisch, ihre Oberflaeche wird
    // im Frame uebersetzt und braucht dafuer die Shopsprache.
    // Parametername wsLang, nicht locale: "locale" wertet die Engine selbst aus
    // und stellt ihre Oberflaeche dann auf Englisch statt Japanisch um — die
    // Uebersetzung im Frame erwartet aber japanische Quelltexte.
    var locale = this.root.getAttribute('data-locale');
    if (locale) url += '&wsLang=' + encodeURIComponent(locale);
    this.rahmen.setAttribute('src', url);

    var self = this;

    this.aufNachricht = function (event) {
      self.empfange(event);
    };
    window.addEventListener('message', this.aufNachricht);
    this.abbau.push(function () {
      window.removeEventListener('message', self.aufNachricht);
    });

    // Kaufwege absichern
    this.sichereKaufwege();

    // Solange die Engine laedt, ist der Kauf gesperrt — es gaebe noch nichts zu
    // speichern.
    this.aktualisiereFormular(null);
    this.setzeZustand('loading', this.t('laedt', 'Der Konfigurator wird geladen …'));
  };

  Konfigurator.prototype.stop = function () {
    clearTimeout(this.speicherWache);
    this.abbau.forEach(function (fn) {
      try {
        fn();
      } catch (e) {
        /* egal */
      }
    });
    this.abbau = [];
  };

  /* ---------------------------------------------------------------- *
   * Nachrichten aus dem Frame
   * ---------------------------------------------------------------- */

  Konfigurator.prototype.empfange = function (event) {
    // Beides pruefen: Herkunft UND konkretes Fenster.
    if (event.origin !== this.appOrigin && event.origin !== 'null') return;
    if (!this.rahmen || event.source !== this.rahmen.contentWindow) return;

    var daten = event.data;
    if (!daten || typeof daten !== 'object') return;
    if (daten.ns !== NS || daten.v !== VERSION) return;
    if (typeof daten.type !== 'string') return;

    var nutz = daten.payload && typeof daten.payload === 'object' ? daten.payload : {};

    switch (daten.type) {
      case 'ready':
        clearTimeout(this.ladeWache);
        // Sonderfall: Die Engine hat laenger als die Ladewache gebraucht, die
        // Seite ist auf den manuellen Weg zurueckgefallen — und der Kunde hat
        // dort bereits seinen Link eingetragen. Diesen Wert darf nichts
        // ueberschreiben. Der manuelle Weg bleibt dann gueltig, bis der Kunde
        // das Feld selbst leert.
        if (this.rueckfall && this.manuellHatInhalt()) {
          this.design = null;
          this.setzeZustand(
            'ready',
            this.t(
              'bereit_manuell',
              'Der Konfigurator ist jetzt bereit. Dein eingetragener Link gilt weiter — leere das Feld, wenn du stattdessen hier gestalten möchtest.'
            )
          );
          this.antworteHandschlag();
          this.beobachteManuellesFeld();
          break;
        }
        this.rueckfall = false;
        // Eine frisch gestartete Engine hat kein gespeichertes Design. Ohne
        // dieses Zuruecksetzen bliebe nach einem Neuaufbau des Frames die alte
        // Design-ID stehen und wuerde beim naechsten Kauf mitgeschickt.
        this.design = null;
        this.aktualisiereFormular(null);
        this.zeigeManuellesFeld(false);
        // Die Stickerei wird ab jetzt im Konfigurator gewaehlt.
        this.verbergeStickereiFeld(true);
        this.setzeZustand('ready', this.t('bereit', 'Gestalte deinen Schuh. Beim Hinzufügen zum Warenkorb wird dein Design automatisch gespeichert.'));
        this.antworteHandschlag();
        break;

      case 'resize':
        this.setzeHoehe(nutz.height);
        break;

      case 'dirty':
        // Altes Ergebnis gilt nicht mehr. Trifft das einen laufenden Kaufversuch,
        // wird dieser abgebrochen — der Kunde klickt einfach erneut.
        this.design = null;
        this.aktualisiereFormular(null);
        if (this.speicherLaeuft) this.speicherBeendet();
        this.setzeZustand('dirty', this.t('geaendert', 'Design geändert. Beim Hinzufügen zum Warenkorb wird es gespeichert.'));
        // Sichtbare Rueckmeldung, dass die Aenderung angekommen ist.
        this.pulsieren();
        // Liegt noch ein aelteres Design im Warenkorb? Dann sagen wir das.
        this.pruefeWarenkorb();
        break;

      case 'saving':
        this.setzeZustand('saving', this.t('speichert', 'Dein Design wird gespeichert …'));
        break;

      case 'saved':
        this.uebernehmeDesign(nutz);
        break;

      case 'embroidery':
        // Der Kunde hat die Stickerei im Konfigurator umgeschaltet: Variante
        // und damit auch der angezeigte Preis ziehen sofort mit.
        this.stickereiLive(nutz.value);
        break;

      case 'error':
        // Drei sehr verschiedene Faelle:
        //  * Die Engine kam gar nicht hoch oder kann nicht speichern -> das
        //    Produkt darf nicht unverkaeuflich werden. Zurueck auf den bisherigen
        //    manuellen Weg; der Kauf wird nicht mehr abgefangen.
        //  * Ein Speicherversuch ist gescheitert -> Entwurf bleibt, der Kunde
        //    kann nach einer Korrektur erneut auf „In den Warenkorb legen“
        //    klicken. Ohne Design kommt der Schuh weiterhin nicht in den Korb.
        var meldung = typeof nutz.message === 'string' && nutz.message
          ? nutz.message
          : this.t('fehler', 'Das Design konnte nicht gespeichert werden.');

        if (nutz.code === 'engine_load_failed' || nutz.code === 'save_unavailable') {
          clearTimeout(this.ladeWache);
          this.design = null;
          this.rueckfall = true;
          this.zeigeManuellesFeld(true);
          // Ohne Konfigurator muss der Kunde die Stickerei wieder selbst
          // waehlen koennen.
          this.verbergeStickereiFeld(false);
        }
        this.speicherBeendet();
        this.setzeZustand('error', meldung);
        this.zeigeHinweis(meldung);
        break;
    }
  };

  Konfigurator.prototype.antworteHandschlag = function () {
    this.andenFrame('host-ready');
  };

  Konfigurator.prototype.andenFrame = function (typ) {
    if (!this.rahmen || !this.rahmen.contentWindow) return false;
    try {
      // Ein sandboxed Dokument hat die Herkunft "null" und ist nicht gezielt
      // adressierbar. Die Nachricht enthaelt bewusst keine Daten; der Frame
      // prueft seinerseits Herkunft und Fenster der Elternseite.
      this.rahmen.contentWindow.postMessage(
        { ns: NS, v: VERSION, type: typ, payload: {} },
        '*'
      );
      return true;
    } catch (e) {
      return false;
    }
  };

  Konfigurator.prototype.setzeHoehe = function (wert) {
    if (this.hoehenSperre) return;
    var hoehe = parseInt(wert, 10);
    if (!isFinite(hoehe)) return;
    if (hoehe < MIN_HOEHE) hoehe = MIN_HOEHE;
    if (hoehe > MAX_HOEHE) hoehe = MAX_HOEHE;
    // Totzone klein halten: eine grosse Totzone laesst den Frame dauerhaft ein
    // Stueck zu kurz stehen und schneidet Inhalt ab (gemessen: 22 px zu kurz,
    // die Beschriftung der Kategorieleiste fehlte).
    if (this.letzteHoehe && Math.abs(hoehe - this.letzteHoehe) < 4) return;

    // Zweite Bremse gegen eine Resize-Schleife: der Frame darf nicht endlos
    // wachsen, auch wenn die App fehlerhaft immer groessere Werte meldet.
    // Nur grosse Spruenge zaehlen — kleine Nachjustierungen sind normal.
    if (this.letzteHoehe && hoehe > this.letzteHoehe + 24) {
      this.wachstum = (this.wachstum || 0) + 1;
      if (this.wachstum > 12) {
        this.hoehenSperre = true;
        if (window.console && console.warn) {
          console.warn('[ws-asics] Höhenanpassung gestoppt: unplausibles Wachstum.');
        }
        return;
      }
    } else {
      this.wachstum = 0;
    }

    this.letzteHoehe = hoehe;
    this.rahmen.style.height = hoehe + 'px';
  };

  /* ---------------------------------------------------------------- *
   * Gespeichertes Design uebernehmen
   * ---------------------------------------------------------------- */

  Konfigurator.prototype.uebernehmeDesign = function (nutz) {
    var id = nutz.recipeId;
    var stickerei = nutz.embroidery;

    if (typeof id !== 'string' || !ID_MUSTER.test(id)) {
      var m1 = this.t('id_ungueltig', 'Es wurde keine gültige Design-ID zurückgegeben.');
      this.speicherBeendet();
      this.setzeZustand('error', m1);
      this.zeigeHinweis(m1);
      return;
    }
    if (stickerei !== 'yes' && stickerei !== 'no') {
      var m2 = this.t('stickerei_unklar', 'Der Stickerei-Zustand war nicht eindeutig. Bitte erneut versuchen.');
      this.speicherBeendet();
      this.setzeZustand('error', m2);
      this.zeigeHinweis(m2);
      return;
    }

    this.design = {
      recipeId: id,
      embroidery: stickerei,
      thumbnailUrl: this.pruefeThumbnail(nutz.thumbnailUrl, id),
      stickText: stickerei === 'yes' ? sauber(nutz.embroideryText, 120) : ''
    };

    // Reihenfolge zaehlt: erst die Variante umschalten (das Theme rendert dabei
    // Preis und versteckte Varianten-ID neu), dann die Eigenschaften setzen.
    var zielWert = stickerei === 'yes' ? this.wertJa : this.wertNein;
    var zielVariante = this.zielVariante(zielWert);
    var varianteVorher = this.setzeStickereiVariante(stickerei);
    this.aktualisiereFormular(this.design);
    this.rueckfall = false;
    this.zeigeHinweis(null);
    this.setzeZustand('saved', this.t('gespeichert', 'Design gespeichert.'));
    this.pulsieren();
    this.zeigeWarenkorbHinweis();

    // Wartet ein Kaufweg auf dieses Ergebnis? Dann jetzt weiterlaufen lassen.
    this.kaufFortsetzen(varianteVorher, zielVariante);
  };

  Konfigurator.prototype.pruefeThumbnail = function (url, id) {
    if (typeof url !== 'string' || !url) return '';
    try {
      var geprueft = new URL(url);
      if (geprueft.protocol !== 'https:') return '';
      if (geprueft.hostname !== 'api.spectrumcustomizer.com') return '';
      if (geprueft.pathname.indexOf(id) === -1) return '';
      return geprueft.href;
    } catch (e) {
      return '';
    }
  };

  /* ---------------------------------------------------------------- *
   * Shopify-Variante „Stickerei“ mitfuehren
   *
   * Rueckgabe: die Varianten-ID VOR der Umschaltung, wenn tatsaechlich
   * umgeschaltet wurde — sonst null. Damit kann der Kaufweg warten, bis das
   * Theme die neue Variante ins Formular geschrieben hat.
   * ---------------------------------------------------------------- */

  Konfigurator.prototype.variantenId = function () {
    var form = this.form();
    var feld = form ? form.querySelector('[name="id"]') : null;
    return feld ? feld.value : null;
  };

  Konfigurator.prototype.setzeStickereiVariante = function (stickerei) {
    var form = this.form();
    if (!form) return null;
    var bereich = form.closest('.shopify-section') || document;
    var ziel = stickerei === 'yes' ? this.wertJa : this.wertNein;
    var vorher = this.variantenId();

    var auswahl = bereich.querySelector(
      'variant-selects select[name="options[' + this.optionStickerei + ']"]'
    );
    if (!auswahl) {
      // Radiovariante des Themes
      var radios = bereich.querySelectorAll(
        'variant-radios input[type="radio"][name*="' + this.optionStickerei + '"]'
      );
      for (var i = 0; i < radios.length; i++) {
        if (radios[i].value === ziel && !radios[i].checked) {
          radios[i].checked = true;
          radios[i].dispatchEvent(new Event('change', { bubbles: true }));
          return vorher;
        }
      }
      return null;
    }

    if (auswahl.value === ziel) return null;
    var gefunden = false;
    for (var j = 0; j < auswahl.options.length; j++) {
      if (auswahl.options[j].value === ziel) gefunden = true;
    }
    if (!gefunden) return null;
    auswahl.value = ziel;
    // Das Theme haengt am change-Event von <variant-selects>; dadurch werden
    // Preis und versteckte Varianten-ID vom Theme selbst aktualisiert.
    auswahl.dispatchEvent(new Event('change', { bubbles: true }));
    return vorher;
  };

  /* ---------------------------------------------------------------- *
   * Shopify-Auswahl „Stickerei“
   *
   * Die Stickerei wird im Konfigurator gewaehlt, deshalb ist das doppelte
   * Auswahlfeld auf der Produktseite ausgeblendet. Das <select> bleibt im
   * Formular stehen — das Theme braucht es, um Variante und Preis zu
   * berechnen; es wird nur nicht mehr angezeigt.
   * ---------------------------------------------------------------- */

  Konfigurator.prototype.stickereiFeld = function () {
    var form = this.form();
    var bereich = (form && form.closest('.shopify-section')) || document;
    var sel = bereich.querySelector(
      'variant-selects select[name="options[' + this.optionStickerei + ']"]'
    );
    if (sel) return sel.closest('fieldset, .product-form__input, .product-form__controls');
    var radio = bereich.querySelector(
      'variant-radios input[type="radio"][name*="' + this.optionStickerei + '"]'
    );
    if (radio) return radio.closest('fieldset, .product-form__input');
    return null;
  };

  Konfigurator.prototype.verbergeStickereiFeld = function (verbergen) {
    this.stickereiVersteckt = !!verbergen;
    var el = this.stickereiFeld();
    if (!el) return;
    el.classList.toggle('ws-asics-versteckt', !!verbergen);
  };

  // Meldung aus dem Konfigurator: Stickerei ein- oder ausgeschaltet.
  // Verbindlich bleibt der Wert aus dem Speicher-Payload — uebernehmeDesign
  // setzt die Variante dort noch einmal. Hier geht es nur darum, dass Preis
  // und Auswahl sofort zum Konfigurator passen.
  Konfigurator.prototype.stickereiLive = function (wert) {
    if (wert !== 'yes' && wert !== 'no') return;
    this.setzeStickereiVariante(wert);
    if (this.stickereiVersteckt) this.verbergeStickereiFeld(true);
  };

  /* ---------------------------------------------------------------- *
   * Versteckte Formularfelder
   * ---------------------------------------------------------------- */

  Konfigurator.prototype.feld = function (name) {
    var form = this.form();
    if (!form) return null;
    // Auch textarea und select beruecksichtigen: das bestehende Pflichtfeld
    // „Farbvariante (Link)“ ist ein <textarea>, nicht ein <input>.
    var attr = '[name="properties[' + name.replace(/"/g, '\\"') + ']"]';
    var sel = 'input' + attr + ', textarea' + attr + ', select' + attr;
    var imForm = form.querySelector(sel);
    if (imForm) return imForm;
    // Felder koennen per form="" ausserhalb des Formulars liegen.
    var aussen = document.querySelectorAll(sel);
    for (var i = 0; i < aussen.length; i++) {
      if (aussen[i].getAttribute('form') === this.formId) return aussen[i];
    }
    return null;
  };

  Konfigurator.prototype.setzeFeld = function (name, wert) {
    var el = this.feld(name);
    if (!el) return;
    // Das bestehende Pflicht-Textarea „Farbvariante (Link)“ bleibt erhalten; es
    // wird jetzt automatisch befuellt statt vom Kunden abgetippt.
    el.value = wert;
    if (el.tagName === 'TEXTAREA' || el.type === 'hidden' || el.type === 'text') {
      el.setAttribute('value', wert);
    }
  };

  // Wie setzeFeld, aber ein leeres Feld wird abgeschaltet.
  // Gemessen am 06.09.2026: Shopify uebernimmt ein leer abgesendetes Feld als
  // leere Eigenschaft in die Warenkorbzeile („Stickerei-Text": ""). Ein
  // deaktiviertes Feld wird gar nicht erst mitgesendet — ohne Stickerei steht
  // in der Zeile also nichts davon.
  Konfigurator.prototype.setzeOptionalesFeld = function (name, wert) {
    var el = this.feld(name);
    if (!el) return;
    el.value = wert;
    el.setAttribute('value', wert);
    el.disabled = !wert;
  };

  Konfigurator.prototype.aktualisiereFormular = function (design) {
    var link = design
      ? 'https://b2b.asics.com/simulations/jp/fw/spectrum-volleyball.html?recipeId=' +
        encodeURIComponent(design.recipeId)
      : '';
    var pdf = design
      ? 'https://api.spectrumcustomizer.com/asics/customer-design/' +
        encodeURIComponent(design.recipeId) +
        '/pdf'
      : '';

    // Design-ID und PDF wie den Stickereitext behandeln: leer abgesendet wuerde
    // Shopify sie als leere Eigenschaft in die Bestellzeile uebernehmen.
    // „Farbvariante (Link)“ ist davon ausgenommen — das ist das bestehende
    // Pflichtfeld der Vorlage und muss immer mitgehen.
    this.setzeOptionalesFeld(this.propId, design ? design.recipeId : '');
    this.setzeFeld(this.propLink, link);
    this.setzeOptionalesFeld(this.propPdf, pdf);

    // Stickereitext. Ohne Stickerei bleibt das Feld leer.
    this.setzeOptionalesFeld(this.propStickText, design ? design.stickText || '' : '');
  };

  /* ---------------------------------------------------------------- *
   * Bisheriges manuelles Linkfeld
   *
   * Das Pflicht-Textarea „Farbvariante (Link)“ bleibt in der Vorlage stehen und
   * wird weiter mitgesendet — nur eben automatisch befuellt. Solange der
   * Konfigurator traegt, ist es ausgeblendet. Faellt die App aus, wird es wieder
   * sichtbar, damit der bisherige Kaufweg erhalten bleibt.
   *
   * Wichtig: Ein ausgeblendetes required-Feld erzeugt sonst einen unsichtbaren
   * Fokusfehler beim Absenden. Deshalb wird required beim Ausblenden entfernt
   * und beim Einblenden wieder gesetzt.
   * ---------------------------------------------------------------- */

  Konfigurator.prototype.manuellesFeld = function () {
    var id = this.root.getAttribute('data-manual-field');
    if (!id) return null;
    return document.getElementById(id);
  };

  Konfigurator.prototype.manuellHatInhalt = function () {
    var feld = this.manuellesFeld();
    return !!(feld && typeof feld.value === 'string' && feld.value.trim() !== '');
  };

  // Nach einem spaeten 'ready' bleibt der manuelle Weg gueltig. Leert der Kunde
  // das Feld doch noch, uebernimmt der Konfigurator.
  Konfigurator.prototype.beobachteManuellesFeld = function () {
    var feld = this.manuellesFeld();
    if (!feld || this.manuellBeobachtet) return;
    this.manuellBeobachtet = true;
    var self = this;
    var aufEingabe = function () {
      if (self.manuellHatInhalt()) return;
      feld.removeEventListener('input', aufEingabe);
      self.manuellBeobachtet = false;
      self.rueckfall = false;
      self.aktualisiereFormular(null);
      self.zeigeManuellesFeld(false);
      self.verbergeStickereiFeld(true);
      self.setzeZustand('ready', self.t('bereit', 'Gestalte deinen Schuh. Beim Hinzufügen zum Warenkorb wird dein Design automatisch gespeichert.'));
    };
    feld.addEventListener('input', aufEingabe);
    this.abbau.push(function () {
      feld.removeEventListener('input', aufEingabe);
    });
  };

  Konfigurator.prototype.zeigeManuellesFeld = function (zeigen) {
    var feld = this.manuellesFeld();
    document.body.classList.toggle('ws-asics-auto', !zeigen);
    // Nur im Ausfall werden die Produktfotos wieder eingeblendet; im
    // Normalbetrieb zeigt der Konfigurator den Schuh in 3D.
    document.body.classList.toggle('ws-asics-rueckfall', !!zeigen);
    if (!feld) return;
    if (zeigen) {
      if (feld.getAttribute('data-ws-required') === '1') {
        feld.setAttribute('required', 'required');
        feld.removeAttribute('data-ws-required');
      }
    } else if (feld.hasAttribute('required')) {
      feld.removeAttribute('required');
      feld.setAttribute('data-ws-required', '1');
    }
  };

  /* ---------------------------------------------------------------- *
   * Kaufwege
   *
   * Gesperrt wird die Schaltflaeche nur, solange es nichts zu speichern gibt
   * (Engine laedt) oder waehrend gespeichert wird. In allen anderen Faellen
   * bleibt sie bedienbar — der Klick selbst loest das Speichern aus.
   * ---------------------------------------------------------------- */

  Konfigurator.prototype.kaufButton = function () {
    var form = this.form();
    if (!form) return null;
    return form.querySelector('button[name="add"], button[type="submit"]');
  };

  Konfigurator.prototype.sperreKauf = function (sperren) {
    this.gesperrt = !!sperren;
    var btn = this.kaufButton();
    if (btn) {
      // Eine deaktivierte Schaltflaeche kann gar nicht erst absenden; damit ist
      // die Sperre unabhaengig von der Reihenfolge fremder Listener.
      if (sperren) {
        // Vorzustand merken: war der Knopf schon vom Theme deaktiviert (etwa
        // weil die Variante ausverkauft ist), darf unser Entsperren ihn nicht
        // wieder freigeben.
        if (btn.getAttribute('data-ws-blocked') !== '1') {
          btn.setAttribute('data-ws-vorher', btn.disabled ? 'aus' : 'an');
        }
        btn.setAttribute('disabled', 'disabled');
        btn.setAttribute('aria-disabled', 'true');
        btn.setAttribute('data-ws-blocked', '1');
      } else if (btn.getAttribute('data-ws-blocked') === '1') {
        var vorher = btn.getAttribute('data-ws-vorher');
        btn.removeAttribute('data-ws-blocked');
        btn.removeAttribute('data-ws-vorher');
        if (vorher !== 'aus') {
          btn.removeAttribute('disabled');
          btn.removeAttribute('aria-disabled');
        }
      }
    }
    this.root.setAttribute('data-ws-locked', sperren ? '1' : '0');
  };

  Konfigurator.prototype.aktualisiereSperre = function () {
    this.sperreKauf(this.zustand === 'loading' || this.speicherLaeuft);
  };

  Konfigurator.prototype.zeigeSpinner = function (an) {
    var btn = this.kaufButton();
    if (!btn) return;
    var spinner = btn.querySelector('.loading-overlay__spinner');
    if (spinner) spinner.classList.toggle('hidden', !an);
    if (an) btn.setAttribute('aria-busy', 'true');
    else btn.removeAttribute('aria-busy');
  };

  Konfigurator.prototype.zeigeHinweis = function (text) {
    if (!this.hinweis) return;
    if (!text) {
      this.hinweis.hidden = true;
      this.hinweis.textContent = this.hinweisStandard;
      return;
    }
    this.hinweis.textContent = text;
    this.hinweis.hidden = false;
  };

  Konfigurator.prototype.hatGueltigesDesign = function () {
    return !!(this.design && ID_MUSTER.test(this.design.recipeId));
  };

  /* ---------------------------------------------------------------- *
   * Rueckmeldung auf Aenderungen
   *
   * Der 3D-Schuh zeigt jede Aenderung sofort — eine zweite Anzeige daneben
   * waere Rauschen. Deshalb nur zweierlei: die Statuskarte hebt sich kurz
   * hervor, damit der Blick sie findet, und sie warnt, wenn im Warenkorb noch
   * ein aelteres Design liegt. Letzteres ist der Teil mit echtem Nutzen:
   * ohne ihn kauft jemand die vorherige Fassung, ohne es zu merken.
   * ---------------------------------------------------------------- */

  Konfigurator.prototype.pulsieren = function () {
    if (!this.statusKarte) return;
    var el = this.statusKarte;
    el.classList.remove('ws-asics__status--puls');
    // Reflow erzwingen, sonst startet die Animation bei schnellen Klicks nicht neu.
    void el.offsetWidth;
    el.classList.add('ws-asics__status--puls');
  };

  Konfigurator.prototype.zeigeWarenkorbHinweis = function () {
    if (!this.warenkorbHinweis) return;
    var alt = this.imWarenkorb;
    var jetzt = this.hatGueltigesDesign() ? this.design.recipeId : null;
    var zeigen = !!alt && alt !== jetzt;
    if (zeigen) {
      var vorlage = this.t(
        'warenkorb_alt',
        'Im Warenkorb liegt noch das Design {id}. Lege den Schuh erneut in den Warenkorb, wenn du das neue Design möchtest.'
      );
      this.warenkorbHinweis.textContent = vorlage.replace('{id}', alt);
    }
    this.warenkorbHinweis.hidden = !zeigen;
  };

  // Einmal je Kauf-/Aenderungszyklus nachsehen, ob das gemerkte Design
  // tatsaechlich noch im Warenkorb liegt — der Kunde kann es entfernt haben.
  Konfigurator.prototype.pruefeWarenkorb = function () {
    var self = this;
    if (!this.imWarenkorb || this.warenkorbPruefung) {
      this.zeigeWarenkorbHinweis();
      return;
    }
    if (typeof fetch !== 'function') {
      this.zeigeWarenkorbHinweis();
      return;
    }
    this.warenkorbPruefung = true;
    fetch('/cart.js', { headers: { Accept: 'application/json' }, credentials: 'same-origin' })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (korb) {
        var drin = false;
        var teile = korb && korb.items ? korb.items : [];
        for (var i = 0; i < teile.length; i++) {
          var eig = teile[i].properties || {};
          if (eig[self.propId] === self.imWarenkorb) drin = true;
        }
        if (!drin) self.imWarenkorb = null;
      })
      .catch(function () { /* Netzfehler: dann eben ohne Pruefung anzeigen */ })
      .then(function () {
        self.warenkorbPruefung = false;
        self.zeigeWarenkorbHinweis();
      });
  };

  Konfigurator.prototype.sichereKaufwege = function () {
    var self = this;

    // Gemeinsame Entscheidung fuer alle Kaufwege.
    var abfangen = function (e, art, el) {
      // Konfigurator ausgefallen: der bisherige manuelle Weg gilt wieder.
      if (self.rueckfall) {
        // Ausnahme: „Oft zusammen gekauft“ sendet das Formular nicht ab und
        // umgeht damit auch die Browserpruefung des Pflichtfeldes. Ohne
        // eingetragenen Link kaeme der Schuh dort ganz ohne Farbangabe in den
        // Warenkorb.
        if (art === 'fbt' && !self.manuellHatInhalt()) {
          e.preventDefault();
          e.stopPropagation();
          e.stopImmediatePropagation();
          var hinweis = self.t('link_fehlt', 'Bitte trage zuerst deinen Farbvarianten-Link ein.');
          self.setzeZustand(self.zustand, hinweis, true);
          self.zeigeHinweis(hinweis);
          var feld = self.manuellesFeld();
          if (feld && feld.scrollIntoView) feld.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
        return;
      }
      // Design liegt vor: normaler Kauf, nichts abfangen. Nur mitschreiben,
      // welches Design damit im Warenkorb landet — sonst warnt die Karte
      // gleich vor einem Design, das der Kunde eben selbst nachgelegt hat.
      if (self.hatGueltigesDesign()) {
        self.imWarenkorb = self.design.recipeId;
        self.zeigeWarenkorbHinweis();
        return;
      }

      e.preventDefault();
      e.stopPropagation();
      e.stopImmediatePropagation();

      if (self.speicherLaeuft) return; // laeuft schon, Doppelklick verwerfen
      if (self.zustand === 'loading') {
        self.meldeWarten();
        return;
      }
      self.kaufWegStarten(art, el);
    };

    // 1) Normaler Submit (Desktop und mobile Kaufleiste nutzen dasselbe Formular).
    //    Auf DOCUMENT mit capture:true, weil Listener am Formular selbst in
    //    Registrierungsreihenfolge laufen — dieselbe Technik nutzt bereits der
    //    Mengenbuendel-Block im Theme.
    var aufSubmit = function (e) {
      var form = self.form();
      if (!form || e.target !== form) return;
      abfangen(e, 'form', null);
    };
    document.addEventListener('submit', aufSubmit, true);
    this.abbau.push(function () {
      document.removeEventListener('submit', aufSubmit, true);
    });

    // 2) Klick auf den Kaufbutton (falls ein fremder Pfad per Klick absendet).
    var aufKlick = function (e) {
      var ziel = e.target;
      if (!(ziel && ziel.closest)) return;

      // Hauptkaufbutton
      var btn = self.kaufButton();
      if (btn && (ziel === btn || btn.contains(ziel))) {
        abfangen(e, 'form', btn);
        return;
      }

      // 3) „Oft zusammen gekauft“: eigener /cart/add.js-Pfad ohne Formular-Submit.
      //    Nur abfangen, wenn der Hauptschuh mitgekauft werden soll.
      //    Zubehoer allein bleibt weiterhin moeglich.
      var fbtBtn = ziel.closest('[data-fbt-add]');
      if (fbtBtn) {
        var fbtRoot = fbtBtn.closest('[data-fbt]');
        var mainCheck = fbtRoot ? fbtRoot.querySelector('[data-fbt-main-check]') : null;
        var hauptDabei = mainCheck ? mainCheck.checked && !mainCheck.disabled : false;
        if (!hauptDabei) return;
        abfangen(e, 'fbt', fbtBtn);
      }
    };
    document.addEventListener('click', aufKlick, true);
    this.abbau.push(function () {
      document.removeEventListener('click', aufKlick, true);
    });

    // 4) Wenn das Theme den Kaufbutton neu rendert (Variantenwechsel), Sperre
    //    und Spinner erneut anwenden.
    var beobachter = new MutationObserver(function () {
      if (self.gesperrt) self.sperreKauf(true);
      if (self.speicherLaeuft) self.zeigeSpinner(true);
    });
    var form = this.form();
    if (form) {
      beobachter.observe(form, { childList: true, subtree: true });
      this.abbau.push(function () {
        beobachter.disconnect();
      });
    }

    // 5) Das Theme rendert die Variantenauswahl bei jedem Wechsel neu. Danach
    //    muss das Stickerei-Feld erneut ausgeblendet werden.
    var feld = this.stickereiFeld();
    var behaelter = feld && feld.parentElement;
    if (behaelter) {
      var beobachter2 = new MutationObserver(function () {
        if (self.stickereiVersteckt) self.verbergeStickereiFeld(true);
      });
      beobachter2.observe(behaelter, { childList: true, subtree: true });
      this.abbau.push(function () {
        beobachter2.disconnect();
      });
    }
  };

  /* ---------------------------------------------------------------- *
   * Kauf mit automatischem Speichern
   * ---------------------------------------------------------------- */

  Konfigurator.prototype.kaufWegStarten = function (art, el) {
    var self = this;

    if (!this.andenFrame('request-save')) {
      var m = this.t('fehler', 'Das Design konnte nicht gespeichert werden.');
      this.setzeZustand('error', m);
      this.zeigeHinweis(m);
      return;
    }

    this.wartenderKauf = { art: art, el: el };
    this.speicherLaeuft = true;
    this.zeigeHinweis(null);
    this.setzeZustand('saving', this.t('speichert', 'Dein Design wird gespeichert …'));
    this.zeigeSpinner(true);

    // Notausstieg, falls der Frame gar nicht mehr antwortet.
    clearTimeout(this.speicherWache);
    this.speicherWache = setTimeout(function () {
      if (!self.speicherLaeuft) return;
      var text = self.t('speicher_fehler', 'Dein Design konnte nicht gespeichert werden. Bitte versuche es erneut.');
      self.speicherBeendet();
      self.setzeZustand('error', text);
      self.zeigeHinweis(text);
    }, SPEICHER_FRIST);
  };

  // Laufenden Vorgang beenden — ohne den Kaufweg fortzusetzen.
  Konfigurator.prototype.speicherBeendet = function () {
    clearTimeout(this.speicherWache);
    this.speicherLaeuft = false;
    this.wartenderKauf = null;
    this.zeigeSpinner(false);
  };

  Konfigurator.prototype.kaufFortsetzen = function (varianteVorher, zielVariante) {
    var warten = this.wartenderKauf;
    this.speicherBeendet();
    if (!warten) return;
    if (!this.hatGueltigesDesign()) return;

    var self = this;
    // Ab hier liegt dieses Design (gleich) im Warenkorb.
    this.imWarenkorb = this.design.recipeId;
    this.zeigeWarenkorbHinweis();
    this.warteAufVariante(varianteVorher, zielVariante, function (erfolgreich) {
      if (!erfolgreich) {
        // Das Theme hat die Variante nicht umgeschaltet. Lieber gar nichts in
        // den Warenkorb legen als die falsche Ausfuehrung.
        self.imWarenkorb = null;
        var text = self.t('variante_haengt', 'Die Auswahl konnte nicht aktualisiert werden. Bitte lade die Seite neu und versuche es erneut.');
        self.setzeZustand('error', text);
        self.zeigeHinweis(text);
        return;
      }
      self.aktualisiereSperre();
      if (warten.art === 'fbt') {
        if (warten.el && document.contains(warten.el)) warten.el.click();
        return;
      }
      // Denselben Weg noch einmal gehen, den der Kunde gewaehlt hat. Der
      // Abfang-Listener laesst ihn jetzt durch, weil ein Design vorliegt.
      var btn = self.kaufButton();
      if (btn && !btn.disabled) {
        btn.click();
        return;
      }
      var form = self.form();
      if (form && form.requestSubmit) form.requestSubmit();
    });
  };

  // Nach dem Umschalten der Stickerei-Option rendert das Theme die versteckte
  // Varianten-ID neu. Erst danach darf abgesendet werden, sonst landet die alte
  // Variante im Warenkorb.
  //
  // fertig(true) = die erwartete Variante steht im Formular.
  // fertig(false) = Frist abgelaufen, das Theme hat nicht umgeschaltet. Dann
  // wird NICHT abgesendet: eine falsche Variante im Warenkorb waere schlimmer
  // als ein Klick, der nichts tut und den Kunden auf den Fehler hinweist.
  Konfigurator.prototype.warteAufVariante = function (vorher, ziel, fertig) {
    if (vorher === null || vorher === undefined) {
      fertig(true);
      return;
    }
    var self = this;
    var start = Date.now();
    var pruefen = function () {
      var jetzt = self.variantenId();
      if (ziel ? String(jetzt) === String(ziel) : jetzt !== vorher) {
        fertig(true);
        return;
      }
      if (Date.now() - start > VARIANTE_FRIST) {
        fertig(false);
        return;
      }
      setTimeout(pruefen, 50);
    };
    setTimeout(pruefen, 50);
  };

  Konfigurator.prototype.meldeWarten = function () {
    var text = this.t('wartet', 'Der Konfigurator wird noch geladen. Bitte einen Moment.');
    this.setzeZustand(this.zustand, text, true);
    if (this.statusKarte && this.statusKarte.scrollIntoView) {
      this.statusKarte.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  };

  /* ---------------------------------------------------------------- *
   * Anzeige
   * ---------------------------------------------------------------- */

  Konfigurator.prototype.setzeZustand = function (zustand, text, nurText) {
    if (!nurText) this.zustand = zustand;
    this.root.setAttribute('data-ws-state', this.zustand);
    if (this.statusText && text) this.statusText.textContent = text;

    var hatDesign = this.hatGueltigesDesign();
    if (this.statusId) {
      this.statusId.textContent = hatDesign ? this.design.recipeId : '';
      this.statusId.hidden = !hatDesign;
    }
    if (this.statusPdf) {
      if (hatDesign) {
        this.statusPdf.href =
          'https://api.spectrumcustomizer.com/asics/customer-design/' +
          encodeURIComponent(this.design.recipeId) +
          '/pdf';
        this.statusPdf.hidden = false;
      } else {
        this.statusPdf.removeAttribute('href');
        this.statusPdf.hidden = true;
      }
    }
    if (this.statusBild) {
      if (hatDesign && this.design.thumbnailUrl) {
        this.statusBild.src = this.design.thumbnailUrl;
        this.statusBild.hidden = false;
      } else {
        this.statusBild.removeAttribute('src');
        this.statusBild.hidden = true;
      }
    }

    this.aktualisiereSperre();
  };

  /* ================================================================== *
   * Auf- und Abbau (auch im Theme-Editor)
   * ================================================================== */

  // In einer Schnellansicht laesst sich nichts gestalten, und die Listener auf
  // document wuerden nach dem Schliessen weiterlaufen. Stylesheet und Skript
  // liegen deshalb im Layout und werden dort gar nicht mitgeliefert — diese
  // Pruefung ist die zweite Absicherung, falls das Markup doch einmal in einem
  // Modal landet.
  var SCHNELLANSICHT = 'quick-add-modal, .quick-add-modal, [id^="QuickAdd-"], .quick-order-list';

  function aufbauen(bereich) {
    var wurzeln = (bereich || document).querySelectorAll('[data-ws-asics]');
    for (var i = 0; i < wurzeln.length; i++) {
      var wurzel = wurzeln[i];
      if (instanzen.has(wurzel)) continue; // keine doppelten Listener
      if (wurzel.closest && wurzel.closest(SCHNELLANSICHT)) {
        // Markup entfernen: ohne Stylesheet ist es unsichtbar, ohne Frame
        // nutzlos. Der Kauf laeuft dort ueber das Pflicht-Linkfeld wie frueher.
        if (wurzel.parentNode) wurzel.parentNode.removeChild(wurzel);
        continue;
      }
      var instanz = new Konfigurator(wurzel);
      instanzen.set(wurzel, instanz);
      instanz.start();
    }
  }

  function abbauen(bereich) {
    var wurzeln = (bereich || document).querySelectorAll('[data-ws-asics]');
    for (var i = 0; i < wurzeln.length; i++) {
      var instanz = instanzen.get(wurzeln[i]);
      if (instanz) {
        instanz.stop();
        instanzen.delete(wurzeln[i]);
      }
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      aufbauen(document);
    });
  } else {
    aufbauen(document);
  }

  // Theme-Editor: Section neu geladen oder entfernt.
  document.addEventListener('shopify:section:load', function (e) {
    abbauen(e.target);
    aufbauen(e.target);
  });
  document.addEventListener('shopify:section:unload', function (e) {
    abbauen(e.target);
  });
})();
